import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  StarIcon,
  Cog6ToothIcon,
  InformationCircleIcon,
  FolderIcon,
  BuildingOffice2Icon,
  TagIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { tasksApi } from "../../../api/tasks";
import { OverdueRecurrenceDialog } from "./OverdueRecurrenceDialog";
import { generateAIContent } from "../../../api/ai/generator";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { CaseTagSelector } from "../../../components/dashboard/cases/CaseTagSelector";
import toast from "react-hot-toast";
import {
  mergeTodoWorkspaceParams,
  parseTodoFilter,
  parseTodoGroupBy,
  parseTodoMine,
  parseTodoTodayPlan,
  parseTodoCreate,
} from "./todoWorkspaceSearchParams";
import { TodoWorkspaceQuickActions } from "./todoWorkspaceActions";
import { TODO_TASK_TITLE_MAX_LENGTH } from "./todoTaskLimits";
import {
  TodoModuleTagField,
  moduleTagsFromTask,
  moduleTagsPayload,
} from "./TodoModuleTagField";
import {
  DateQuickPicker,
  formatSmartDueDate,
  formatSmartReminder,
} from "./todoDateQuickPick";
import { PromangRichTextEditor } from "../../../components/utils/PromangRichTextEditor";
import ModernEmployeeSelector from "../../../components/utils/Selector/ModernEmployeeSelector";
import { UnsavedChangesDialog } from "../../../components/utils/Prompt";
import { HseDocumentSelector } from "../../../components/utils/moduleSelectors/hseDocumentSelector";
import { PMDialog } from "../../../components/utils/PMDialog";
import {
  planGhostBtnClass,
  planPrimaryBtnClass,
} from "../../../components/dashboard/projects/projectPlanTheme";
import "../../../components/utils/PromangRichTextEditor.css";
import "../../../components/dashboard/projects/projectPlanSitelane.css";
import "./TodoTaskList.css";

const TODO_PAGE_SIZE = 30;

function normalizeRichTextHtml(html) {
  if (!html) return "";
  const text = html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  return html;
}

import { RECURRENCE_OPTIONS } from "./todoConstants";
/* Priority + status taxonomy adapted from the Claude Design `ToDo` handoff
 * (todo/project/data.jsx). Glyphs and colours come straight from the prototype
 * so the dark surface keeps its calm, dev-tool feel. */
const PRIORITIES = [
  { id: "urgent", labelKey: "todo.priorityUrgent", color: "#ef4444", glyph: "!!!" },
  { id: "high", labelKey: "todo.priorityHigh", color: "#f97316", glyph: "!!" },
  { id: "med", labelKey: "todo.priorityMed", color: "#eab308", glyph: "!" },
  { id: "low", labelKey: "todo.priorityLow", color: "#64748b", glyph: "·" },
];

const STATUSES = [
  { id: "doing", labelKey: "todo.statusDoing", glyph: "◑", color: "#a78bfa" },
  { id: "todo", labelKey: "todo.statusTodo", glyph: "◔", color: "#94a3b8" },
  { id: "done", labelKey: "todo.statusDone", glyph: "●", color: "#10b981" },
];

const STATUS_ORDER = ["doing", "todo", "done"];

function findPriority(id) {
  if (!id) return PRIORITIES.find((p) => p.id === "med");
  const normalized = String(id).toLowerCase();
  if (normalized === "medium") return PRIORITIES.find((p) => p.id === "med");
  return PRIORITIES.find((p) => p.id === normalized) || PRIORITIES.find((p) => p.id === "med");
}

function findStatus(id) {
  return STATUSES.find((s) => s.id === id) || STATUSES[1];
}

function deriveStatus(task) {
  if (task.finished) return "done";
  if (task.on_today_plan) return "doing";
  return "todo";
}

function shortTaskId(task) {
  if (task.task_number) return task.task_number;
  if (typeof task.id === "string" && task.id.startsWith("temp-")) return "T-…";
  return `T-${task.id}`;
}

function initialsFor(name) {
  if (!name) return "·";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "·";
  const first = parts[0][0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] || "" : "";
  return (first + last).toUpperCase() || "·";
}

const AVATAR_PALETTE = [
  "#8b5cf6", "#06b6d4", "#f59e0b", "#ec4899", "#10b981",
  "#60a5fa", "#fb923c", "#34d399", "#facc15", "#f472b6",
];

function avatarColor(seed) {
  if (seed === null || seed === undefined) return AVATAR_PALETTE[0];
  const key = String(seed);
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
}

function personForTask(task) {
  const assignee = Array.isArray(task.assignees) && task.assignees.length > 0 ? task.assignees[0] : null;
  const name =
    assignee?.name ||
    task.created_by?.name ||
    (task.assigned_to ? `User #${task.assigned_to}` : null);
  const seed = assignee?.id ?? task.assigned_to ?? task.created_by?.id ?? task.id;
  if (!name) return null;
  return { id: seed, name, initials: initialsFor(name), color: avatarColor(seed) };
}

// Prefer the project/customer module link over a plain label tag so it is clear at a glance
// that a task belongs to a project/module (not just a personal todo). `kind` drives the chip icon.
function moduleForTask(task) {
  if (task.project?.name || task.project?.project_number) {
    return {
      id: `project-${task.project.id}`,
      name: task.project.name || task.project.project_number,
      color: "#60a5fa",
      kind: "project",
    };
  }
  if (task.customer?.name) {
    return {
      id: `customer-${task.customer.id}`,
      name: task.customer.name,
      color: "#34d399",
      kind: "customer",
    };
  }
  if (Array.isArray(task.tag_definitions) && task.tag_definitions.length > 0) {
    const tag = task.tag_definitions[0];
    return { id: `tag-${tag.id}`, name: tag.name, color: tag.color || "#a78bfa", kind: "tag" };
  }
  return null;
}

const MODULE_KIND_ICON = {
  project: FolderIcon,
  customer: BuildingOffice2Icon,
  tag: TagIcon,
  shared: UsersIcon,
};

function isItemListLockedTask(task) {
  return !!(task?.description && task.description.includes("Item List ID:"));
}

function isHseDocumentModuleTask(task) {
  if (!task) return false;
  if (task.shared) return false;
  if (task.task_type === "hse_document_read") return true;
  if (task.description && task.description.includes("HSE_DOC_VERSION_ID:")) return true;
  if (task.task_reference_type === "Hse::Document" && task.task_type !== "company_shared") return true;
  return false;
}

function isTaskAssigneesLocked(task) {
  return isItemListLockedTask(task) || isHseDocumentModuleTask(task);
}

function canEditTaskAssignees(task, currentUserId, canManageSharedTasks) {
  if (isTaskAssigneesLocked(task)) return false;
  if (canManageSharedTasks) return true;
  if (task?.shared) return false;
  if (!currentUserId) return false;
  const uid = Number(currentUserId);
  const createdById = Number(task?.created_by?.id ?? task?.created_by ?? 0);
  if (createdById && createdById === uid) return true;
  if (Array.isArray(task.assignees) && task.assignees.some((row) => Number(row.id) === uid)) {
    return true;
  }
  return false;
}

function assigneeTagLabel(name) {
  if (!name) return "@?";
  const first = String(name).trim().split(/\s+/).filter(Boolean)[0];
  return first ? `@${first}` : "@?";
}

function UserAssigneeTag({ name, title }) {
  const label = assigneeTagLabel(name);
  return (
    <span className="td-user-tag" title={title ?? name ?? label}>
      {label}
    </span>
  );
}

function PropertyLabelWithInfo({ label, onInfo, infoAriaLabel }) {
  if (!onInfo) {
    return <div className="td-prop-label">{label}</div>;
  }
  return (
    <div className="td-prop-label td-prop-label-with-info">
      <span>{label}</span>
      <button type="button" className="td-prop-info-btn" onClick={onInfo} aria-label={infoAriaLabel}>
        <InformationCircleIcon className="td-prop-info-icon" aria-hidden />
      </button>
    </div>
  );
}

function PropertyInfoDialog({ open, title, text, onClose, t }) {
  return (
    <PMDialog open={open} onClose={onClose} title={title} size="sm" dense zIndex={2400}>
      <p className="td-prop-info-dialog-text">{text}</p>
      <div className="td-prop-info-dialog-actions">
        <button type="button" className={planPrimaryBtnClass} onClick={onClose}>
          {t("common.close")}
        </button>
      </div>
    </PMDialog>
  );
}

function hasHseDocumentLink(task) {
  return !!(task?.task_reference_type === "Hse::Document" && task?.task_reference_id);
}

/* ─────────────────────────────────────────────
 * Atomic display components — Avatar, Glyphs, Dates
 * ──────────────────────────────────────────── */

function Avatar({ person, size = 18, ring = false, title }) {
  if (!person) return null;
  const dim = { width: size, height: size, fontSize: Math.max(8, size * 0.42) };
  return (
    <span
      className="td-avatar"
      style={{
        ...dim,
        background: person.color,
        boxShadow: ring ? "0 0 0 2px var(--td-bg)" : "none",
      }}
      title={title ?? person.name}
    >
      {person.initials}
    </span>
  );
}

function PriorityGlyph({ priority, t }) {
  const p = findPriority(priority);
  if (!p) return null;
  return (
    <span className="td-priority" style={{ color: p.color }} title={t(p.labelKey)}>
      {p.glyph}
    </span>
  );
}

function StatusGlyph({ status, size = 14, t }) {
  const s = findStatus(status);
  return (
    <span
      className="td-status-glyph"
      style={{ color: s.color, fontSize: size, lineHeight: 1 }}
      title={t(s.labelKey)}
    >
      {s.glyph}
    </span>
  );
}

function ModuleChip({ moduleObj, t }) {
  if (!moduleObj) {
    return (
      <span className="td-chip td-prop-value-empty">
        <span className="td-chip-hash">—</span>
      </span>
    );
  }
  const kind = moduleObj.kind || "tag";
  const Icon = MODULE_KIND_ICON[kind];
  const kindLabel = t ? t(`todo.moduleKind.${kind}`) : null;
  const title = kindLabel ? `${kindLabel}: ${moduleObj.name}` : moduleObj.name;
  return (
    <span className={`td-chip td-chip-${kind}`} title={title}>
      {Icon ? (
        <Icon
          className="td-chip-kind-icon"
          style={{ width: 12, height: 12, color: moduleObj.color, flex: "0 0 auto" }}
          aria-hidden
        />
      ) : (
        <span className="td-chip-dot" style={{ background: moduleObj.color }} />
      )}
      <span className="td-chip-text">
        {kind === "tag" ? <span className="td-chip-hash">#</span> : null}
        {moduleObj.name}
      </span>
      {moduleObj.shared ? (
        <UsersIcon
          className="td-chip-shared-icon"
          style={{ width: 11, height: 11, color: "#2b6cff", flex: "0 0 auto" }}
          title={t ? t("todo.sharedTaskBadge") : undefined}
          aria-hidden
        />
      ) : null}
    </span>
  );
}

function DateLabel({ date, reminder = false, t }) {
  if (!date) return <span className="td-date-empty">—</span>;
  const d = new Date(reminder ? date : `${date}T12:00:00`);
  if (Number.isNaN(d.getTime())) return <span className="td-date-empty">—</span>;

  if (reminder) {
    return (
      <span className="td-date td-date-soon">
        <span className="td-date-icon">◔</span>
        {formatSmartReminder(date, t)}
      </span>
    );
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dCopy = new Date(d);
  dCopy.setHours(0, 0, 0, 0);
  const diffDays = Math.round((dCopy - today) / 86400000);
  let label;
  let tone = "default";
  if (diffDays < 0) {
    label = `${Math.abs(diffDays)}d`;
    tone = "overdue";
  } else if (diffDays === 0) {
    label = t("todo.dateToday");
    tone = "today";
  } else if (diffDays === 1) {
    label = t("todo.dateTomorrow");
    tone = "soon";
  } else if (diffDays < 7) {
    label = `${diffDays}d`;
    tone = "soon";
  } else {
    label = formatSmartDueDate(date, t);
  }
  return (
    <span className={`td-date td-date-${tone}`}>
      <span className="td-date-icon">◷</span>
      {label}
    </span>
  );
}

function formatDateTimeShort(value, t) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const that = new Date(d);
  that.setHours(0, 0, 0, 0);
  const diff = Math.round((today - that) / 86400000);
  if (diff === 0) {
    const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    return t("todo.dateTimeToday", { time });
  }
  if (diff > 0 && diff < 7) return t("todo.dateTimeDaysAgo", { count: diff });
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

const MENTION_RE = /([@#])([\w\-æøåÆØÅ]+)/g;

function MentionRender({ text }) {
  if (!text) return null;
  const parts = [];
  let last = 0;
  let key = 0;
  let m;
  while ((m = MENTION_RE.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const sigil = m[1];
    const name = m[2];
    parts.push(
      <span
        key={`mn-${key++}`}
        className="td-mention"
        style={{ "--td-mc": sigil === "@" ? "#06b6d4" : "#a78bfa" }}
      >
        {sigil}
        {name}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

/* ─────────────────────────────────────────────
 * Outside-click popover anchor
 * ──────────────────────────────────────────── */

function usePopover(initial = null) {
  const [pop, setPop] = useState(initial);
  const ref = useRef(null);
  useEffect(() => {
    if (!pop) return undefined;
    const handler = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setPop(null);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [pop]);
  return [pop, setPop, ref];
}

/* ─────────────────────────────────────────────
 * Create modal — mention-aware title/desc + chip rail
 * ──────────────────────────────────────────── */

function useCompactCreateLayout() {
  const [compact, setCompact] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (event) => setCompact(event.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return compact;
}

function CreateModal({ open, onClose, onCreate, t }) {
  const compactCreate = useCompactCreateLayout();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [priority, setPriority] = useState("med");
  const [dueDate, setDueDate] = useState(null);
  const [reminder, setReminder] = useState(null);
  const [onTodayPlan, setOnTodayPlan] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedModuleTags, setSelectedModuleTags] = useState([]);
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);
  const [pop, setPop, popRef] = usePopover(null);

  useEffect(() => {
    if (open) {
      setTitle("");
      setDescription("");
      setDescriptionExpanded(false);
      setPriority("med");
      setDueDate(null);
      setReminder(null);
      setOnTodayPlan(false);
      setSelectedAssignees([]);
      setSelectedTagIds([]);
      setSelectedModuleTags([]);
      setAssigneePickerOpen(false);
      setPop(null);
    }
  }, [open, setPop]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const priorityObj = findPriority(priority);
  const hasDescription = !!normalizeRichTextHtml(description);
  const showDescriptionEditor = !compactCreate || descriptionExpanded;

  const submit = () => {
    if (!title.trim()) return;
    onCreate({
      title: title.trim(),
      description: normalizeRichTextHtml(description),
      priority,
      due_date: dueDate || null,
      reminder_at: reminder ? new Date(reminder).toISOString() : null,
      on_today_plan: !!onTodayPlan,
      assignee_user_ids: selectedAssignees.map((row) => row.id),
      tag_ids: selectedTagIds,
      module_tags: moduleTagsPayload(selectedModuleTags),
    });
  };

  const onTitleKey = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      className="project-plan-sitelane td-app td-create-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div className="td-create-card" onMouseDown={(e) => e.stopPropagation()} ref={popRef}>
        <div className="td-create-head">
          <span>{t("todo.createTitle")}</span>
          <button
            type="button"
            className={`${planGhostBtnClass} td-icon-btn`}
            onClick={onClose}
            aria-label={t("common.close")}
          >
            ×
          </button>
        </div>

        <div className="td-create-body">
          <div className="td-form-field">
            <label className="td-form-label" htmlFor="td-create-title-input">
              {t("todo.createTitlePlaceholder")}
            </label>
            <input
              id="td-create-title-input"
              autoFocus
              className="td-field"
              value={title}
              maxLength={TODO_TASK_TITLE_MAX_LENGTH}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={onTitleKey}
              placeholder={t("todo.createTitlePlaceholder")}
            />
          </div>

          <div className="td-form-field">
            {showDescriptionEditor ? (
              <>
                <div className="td-create-desc-head">
                  <span className="td-form-label">{t("todo.descriptionLabel")}</span>
                  {compactCreate ? (
                    <button
                      type="button"
                      className="td-create-desc-collapse"
                      onClick={() => setDescriptionExpanded(false)}
                    >
                      {t("todo.hideDescription")}
                    </button>
                  ) : null}
                </div>
                <PromangRichTextEditor
                  value={description}
                  onChange={setDescription}
                  placeholder={t("todo.descriptionPlaceholder")}
                  size="sm"
                  framed
                  templatesEnabled={false}
                  wrapperClassName="td-rich-editor"
                />
              </>
            ) : (
              <button
                type="button"
                className={`td-create-desc-toggle ${hasDescription ? "has-content" : ""}`}
                onClick={() => setDescriptionExpanded(true)}
                aria-expanded={false}
              >
                <span>{hasDescription ? t("todo.descriptionAdded") : t("todo.addDescription")}</span>
                <span className="td-create-desc-toggle-chev" aria-hidden>
                  ›
                </span>
              </button>
            )}
          </div>
        </div>

        <div className="td-create-tags">
          <div className="td-anchor">
            <button
              type="button"
              className={`${planGhostBtnClass} td-chip-btn ${priority !== "med" ? "is-set" : ""}`}
              onClick={() => setPop(pop === "priority" ? null : "priority")}
            >
              <span className="td-create-tag-icon" style={{ color: priorityObj.color }}>
                {priorityObj.glyph}
              </span>
              <span>{t(priorityObj.labelKey)}</span>
            </button>
            {pop === "priority" && (
              <div className="td-popover">
                {PRIORITIES.map((p) => (
                  <div
                    key={p.id}
                    className={`td-popover-item ${p.id === priority ? "is-active" : ""}`}
                    onClick={() => {
                      setPriority(p.id);
                      setPop(null);
                    }}
                  >
                    <span
                      style={{
                        color: p.color,
                        fontFamily: "var(--td-font-mono)",
                        width: 18,
                        textAlign: "center",
                        display: "inline-block",
                      }}
                    >
                      {p.glyph}
                    </span>
                    <span>{t(p.labelKey)}</span>
                    {p.id === priority && <span className="td-popover-check">✓</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="td-anchor">
            <button
              type="button"
              className={`${planGhostBtnClass} td-chip-btn ${dueDate ? "is-set" : ""}`}
              onClick={() => setPop(pop === "due" ? null : "due")}
            >
              <span className="td-create-tag-icon">◷</span>
              <span>{dueDate ? formatSmartDueDate(dueDate, t) : t("todo.dueDate")}</span>
            </button>
            {pop === "due" && (
              <div className="td-popover">
                <DateQuickPicker
                  mode="date"
                  value={dueDate}
                  onChange={(v) => {
                    setDueDate(v);
                    setPop(null);
                  }}
                  onClear={() => {
                    setDueDate(null);
                    setPop(null);
                  }}
                  t={t}
                />
              </div>
            )}
          </div>

          <div className="td-anchor">
            <button
              type="button"
              className={`${planGhostBtnClass} td-chip-btn ${reminder ? "is-set" : ""}`}
              onClick={() => setPop(pop === "reminder" ? null : "reminder")}
            >
              <span className="td-create-tag-icon">◔</span>
              <span>{reminder ? formatSmartReminder(reminder, t) : t("todo.reminder")}</span>
            </button>
            {pop === "reminder" && (
              <div className="td-popover">
                <DateQuickPicker
                  mode="datetime"
                  value={reminder}
                  onChange={(v) => {
                    setReminder(v);
                    setPop(null);
                  }}
                  onClear={() => {
                    setReminder(null);
                    setPop(null);
                  }}
                  t={t}
                />
              </div>
            )}
          </div>

          <button
            type="button"
            className={`${planGhostBtnClass} td-chip-btn ${onTodayPlan ? "is-set" : ""}`}
            onClick={() => setOnTodayPlan((v) => !v)}
          >
            <span className="td-create-tag-icon">◑</span>
            <span>{t("todo.onTodayPlan")}</span>
          </button>

          <div className="td-anchor">
            <button
              type="button"
              className={`${planGhostBtnClass} td-chip-btn ${selectedAssignees.length ? "is-set" : ""}`}
              onClick={() => setAssigneePickerOpen(true)}
            >
              <span className="td-create-tag-icon">◎</span>
              <span>
                {selectedAssignees.length
                  ? t("todo.settingsAddAssignees")
                  : t("todo.createAddAssignees")}
              </span>
            </button>
          </div>
        </div>

        {selectedAssignees.length > 0 ? (
          <div className="td-create-people-tags">
            {selectedAssignees.map((row) => (
              <UserAssigneeTag key={row.id} name={row.name || `#${row.id}`} />
            ))}
          </div>
        ) : null}

        <div className="td-create-labels">
          <span className="td-form-label">{t("todo.labels")}</span>
          <CaseTagSelector
            scopeModule="task"
            variant="inline"
            value={selectedTagIds}
            onChange={setSelectedTagIds}
          />
        </div>

        <div className="td-create-module-row">
          <span className="td-form-label">{t("todo.moduleTagsLabel")}</span>
          <TodoModuleTagField
            compact
            value={selectedModuleTags}
            onChange={setSelectedModuleTags}
          />
        </div>

        <div className="td-create-foot">
          <span className="td-create-foot-hint">
            <kbd>⌘</kbd>
            <kbd>↵</kbd>
            <span style={{ color: "var(--td-text-4)" }}>{t("todo.createSubmitHint")}</span>
          </span>
          <button type="button" className={planPrimaryBtnClass} onClick={submit} disabled={!title.trim()}>
            {t("todo.createSubmit")}
          </button>
        </div>
      </div>

      <ModernEmployeeSelector
        open={assigneePickerOpen}
        useDialog
        multiple
        closeOnSelect={false}
        closeOnOutsideClick={false}
        selectedEmployees={selectedAssignees}
        onClose={() => setAssigneePickerOpen(false)}
        onConfirm={(employees) => {
          setSelectedAssignees(employees);
          setAssigneePickerOpen(false);
        }}
      />
    </div>
  );
}

/* ─────────────────────────────────────────────
 * Status timeline derived from real fields
 * ──────────────────────────────────────────── */

function StatusTimeline({ task, t }) {
  const currentStatus = deriveStatus(task);
  const path = ["todo", "doing", "done"];
  const reached = new Set(["todo"]);
  if (task.on_today_plan || task.finished) reached.add("doing");
  if (task.finished) reached.add("done");
  reached.add(currentStatus);

  const stepWhen = (id) => {
    if (id === "todo") return task.created_at;
    if (id === "done") return task.finished_at || (task.finished ? task.updated_at : null);
    if (id === "doing") return task.on_today_plan ? task.updated_at : null;
    return null;
  };

  return (
    <div className="td-timeline">
      {path.map((id) => {
        const s = findStatus(id);
        const isReached = reached.has(id);
        const isCurrent = currentStatus === id;
        return (
          <div
            key={id}
            className={`td-timeline-step ${isReached ? "is-done" : ""} ${isCurrent ? "is-current" : ""}`}
          >
            <span
              className="td-timeline-glyph"
              style={{
                color: isCurrent
                  ? s.color
                  : isReached
                  ? "var(--td-text-2)"
                  : "var(--td-text-4)",
              }}
            >
              {s.glyph}
            </span>
            <span className="td-timeline-label">{t(s.labelKey)}</span>
            <span className="td-timeline-when">
              {isReached && stepWhen(id) ? formatDateTimeShort(stepWhen(id), t) : ""}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ActivityLog({ task, t }) {
  const entries = useMemo(() => {
    const out = [];
    if (task.created_at) {
      out.push({
        kind: "created",
        at: task.created_at,
        who: task.created_by?.name || t("todo.activityWhoUser"),
      });
    }
    if (task.finished && task.finished_at) {
      out.push({
        kind: "finished",
        at: task.finished_at,
        who: task.created_by?.name || t("todo.activityWhoUser"),
      });
    } else if (task.updated_at && task.updated_at !== task.created_at) {
      out.push({
        kind: "updated",
        at: task.updated_at,
        who: task.created_by?.name || t("todo.activityWhoUser"),
      });
    }
    return out.reverse();
  }, [task, t]);

  if (!entries.length) {
    return <div style={{ fontSize: 12, color: "var(--td-text-4)" }}>{t("todo.activityEmpty")}</div>;
  }

  const iconFor = (kind) => {
    switch (kind) {
      case "created": return "+";
      case "finished": return "●";
      case "updated": return "✎";
      default: return "·";
    }
  };
  const bodyFor = (entry) => {
    switch (entry.kind) {
      case "created":
        return (
          <>
            <span className="td-activity-who">{entry.who}</span> {t("todo.activityCreated")}
          </>
        );
      case "finished":
        return (
          <>
            <span className="td-activity-who">{entry.who}</span> {t("todo.activityFinished")}
          </>
        );
      case "updated":
      default:
        return (
          <>
            <span className="td-activity-who">{entry.who}</span> {t("todo.activityUpdated")}
          </>
        );
    }
  };

  return (
    <div className="td-activity">
      {entries.map((entry, i) => (
        <div key={i} className="td-activity-entry">
          <span className="td-activity-icon">{iconFor(entry.kind)}</span>
          <div className="td-activity-body">
            {bodyFor(entry)}
            <span className="td-activity-when">{formatDateTimeShort(entry.at, t)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
 * Row
 * ──────────────────────────────────────────── */

function TaskRow({ task, isSelected, onOpen, onToggle, onToggleTodayPlan, t }) {
  const status = deriveStatus(task);
  const statusObj = findStatus(status);
  const linkedModule = moduleForTask(task);
  const moduleObj = linkedModule
    ? { ...linkedModule, shared: !!task.shared }
    : task.shared
    ? { id: "shared", name: t("todo.sharedTaskBadge"), color: "#2b6cff", kind: "shared" }
    : null;
  const person = personForTask(task);
  const locked =
    (task.absence_id && !task.absence?.accepted && !task.absence?.denied) ||
    isItemListLockedTask(task) ||
    (isHseDocumentModuleTask(task) && task.finished);

  return (
    <div
      className={`td-row ${task.finished ? "is-done" : ""} ${isSelected ? "is-selected" : ""}`}
      onClick={() => onOpen(task)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen(task);
      }}
    >
      <div
        className={`td-row-check ${locked ? "is-locked" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!locked) onToggle(task.id, task.finished);
        }}
        title={
          locked && task.absence_id
            ? t("todo.lockedAbsence")
            : locked && isItemListLockedTask(task)
            ? t("todo.lockedItemList")
            : locked
            ? t("todo.hseDocumentTaskCannotReopen")
            : task.finished
            ? t("todo.reopen")
            : t("todo.markDone")
        }
      >
        {task.finished ? "✓" : "✓"}
      </div>
      <div className="td-row-id">{shortTaskId(task)}</div>
      <div className="td-row-title">
        <PriorityGlyph priority={task.priority} t={t} />
        <span className="td-row-title-text">{task.title || t("todo.untitledTask")}</span>
        {normalizeRichTextHtml(task.description) ? (
          <span className="td-row-title-meta" title={t("todo.hasDescription")}>
            <span className="td-row-title-meta-glyph">¶</span>
          </span>
        ) : null}
        {task.reminder_at ? (
          <span className="td-row-title-meta" title={t("todo.hasReminder")}>
            <span className="td-row-title-meta-glyph" style={{ color: "var(--td-accent)" }}>
              ◔
            </span>
          </span>
        ) : null}
      </div>
      <div className="td-row-trailing">
        <div className="td-row-status">
          <StatusGlyph status={status} t={t} />
          <span>{t(statusObj.labelKey)}</span>
        </div>
        <div className="td-row-due">
          <DateLabel date={task.due_date} t={t} />
        </div>
        <div className="td-row-people">
          {person ? <Avatar person={person} size={20} ring /> : <span className="td-date-empty">—</span>}
        </div>
        <div className="td-row-module">
          <ModuleChip moduleObj={moduleObj} t={t} />
        </div>
        <button
          type="button"
          className={`td-row-star ${task.on_today_plan ? "is-active" : ""}`}
          aria-pressed={!!task.on_today_plan}
          aria-label={t("todo.onTodayPlan")}
          title={t("todo.onTodayPlan")}
          onClick={(e) => {
            e.stopPropagation();
            onToggleTodayPlan(task.id, !task.on_today_plan);
          }}
        >
          {task.on_today_plan ? (
            <StarIconSolid className="td-row-star-icon" aria-hidden />
          ) : (
            <StarIcon className="td-row-star-icon" aria-hidden />
          )}
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
 * Detail aside — properties + timeline + activity
 * ──────────────────────────────────────────── */

function DetailAside({
  task,
  onClose,
  onUpdate,
  onToggleFinished,
  onDelete,
  taskTagIds,
  setTaskTagIds,
  runTodoAi,
  hoursAvailable,
  setHoursAvailable,
  canManageSharedTasks,
  currentUserId,
  t,
  navigate,
}) {
  const [titleDraft, setTitleDraft] = useState(task.title || "");
  const [descDraft, setDescDraft] = useState(task.description || "");
  const [propPop, setPropPop, asideRef] = usePopover(null);
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const [moduleTags, setModuleTags] = useState([]);
  const [confirmClose, setConfirmClose] = useState(false);
  const [propertyInfo, setPropertyInfo] = useState(null);
  const descSaveTimer = useRef(null);

  const assigneeIds = (rows) =>
    (rows || [])
      .map((row) => row.id)
      .filter(Boolean)
      .sort((a, b) => a - b)
      .join(",");

  const moduleTagKeys = (rows) =>
    moduleTagsPayload(rows || [])
      .map((row) => `${row.module}:${row.module_param}`)
      .sort()
      .join(",");

  const isDirty = useMemo(() => {
    const titleChanged = titleDraft.trim() !== (task.title || "").trim();
    const descChanged =
      normalizeRichTextHtml(descDraft) !== normalizeRichTextHtml(task.description || "");
    const assigneesChanged =
      assigneeIds(selectedAssignees) !== assigneeIds(task.assignees);
    const moduleTagsChanged =
      moduleTagKeys(moduleTags) !== moduleTagKeys(moduleTagsFromTask(task));
    return titleChanged || descChanged || assigneesChanged || moduleTagsChanged;
  }, [titleDraft, descDraft, selectedAssignees, moduleTags, task]);

  const flushPendingEdits = useCallback(() => {
    const nextTitle = titleDraft.trim();
    if (nextTitle && nextTitle !== task.title) {
      onUpdate(task.id, { title: nextTitle });
    }
    if (descSaveTimer.current) clearTimeout(descSaveTimer.current);
    const normalized = normalizeRichTextHtml(descDraft);
    const current = normalizeRichTextHtml(task.description || "");
    if (normalized !== current) {
      onUpdate(task.id, { description: normalized });
    }
  }, [titleDraft, descDraft, task.id, task.title, task.description, onUpdate]);

  const requestClose = useCallback(() => {
    if (assigneePickerOpen) return;
    if (isDirty) {
      setConfirmClose(true);
      return;
    }
    onClose();
  }, [assigneePickerOpen, isDirty, onClose]);

  const saveAndClose = useCallback(() => {
    flushPendingEdits();
    setConfirmClose(false);
    onClose();
  }, [flushPendingEdits, onClose]);

  useEffect(() => {
    setTitleDraft(task.title || "");
    setDescDraft(task.description || "");
    setSelectedAssignees(
      Array.isArray(task.assignees)
        ? task.assignees.map((row) => ({ id: row.id, name: row.name || `#${row.id}` }))
        : []
    );
    setModuleTags(moduleTagsFromTask(task));
  }, [task.id, task.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      if (descSaveTimer.current) clearTimeout(descSaveTimer.current);
    },
    []
  );

  const handleDescChange = useCallback(
    (html) => {
      setDescDraft(html);
      if (descSaveTimer.current) clearTimeout(descSaveTimer.current);
      descSaveTimer.current = setTimeout(() => {
        const normalized = normalizeRichTextHtml(html);
        const current = normalizeRichTextHtml(task.description || "");
        if (normalized !== current) {
          onUpdate(task.id, { description: normalized });
        }
      }, 800);
    },
    [task.id, task.description, onUpdate]
  );

  const status = deriveStatus(task);
  const statusObj = findStatus(status);
  const priorityObj = findPriority(task.priority);
  const linkedModule = moduleForTask(task);
  const moduleObj = linkedModule ? { ...linkedModule, shared: !!task.shared } : null;
  const peopleEditable = canEditTaskAssignees(task, currentUserId, canManageSharedTasks);
  const displayAssignees = peopleEditable ? selectedAssignees : task.assignees || [];
  const creatorName = task.created_by?.name;
  const peopleTags =
    displayAssignees.length > 0
      ? displayAssignees
      : creatorName && !peopleEditable
        ? [{ id: `creator-${task.created_by?.id ?? "x"}`, name: creatorName }]
        : [];
  const sharedInfoText = task.company_task_template_id
    ? `${t("todo.sharedFromTemplateInfo")}\n\n${t("todo.sharedCompletionAny")}`
    : `${t("todo.sharedFromAssigneesInfo")}\n\n${t("todo.sharedCompletionAny")}`;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (assigneePickerOpen || confirmClose) return;
      if (propertyInfo) {
        e.preventDefault();
        setPropertyInfo(null);
        return;
      }
      e.preventDefault();
      requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [assigneePickerOpen, confirmClose, propertyInfo, requestClose]);

  const commitTitle = () => {
    const v = titleDraft.trim();
    if (v && v !== task.title) onUpdate(task.id, { title: v });
  };

  const setProp = (patch) => {
    if (patch.finished === true && !task.finished && onToggleFinished) {
      onToggleFinished(task.id, false);
      setPropPop(null);
      return;
    }
    onUpdate(task.id, patch);
    setPropPop(null);
  };

  return (
    <>
      <button
        type="button"
        className="td-detail-backdrop"
        onClick={requestClose}
        aria-label={t("common.close")}
      />
      <aside className="td-detail-aside" ref={asideRef} role="dialog" aria-modal="true" aria-label={t("todo.taskDetails")}>
      <div className="td-detail-aside-head">
        <div className="td-detail-aside-head-text">
          <p className="td-detail-aside-eyebrow">{t("todo.taskDetails")}</p>
          <h3 className="td-detail-aside-title">
            {titleDraft.trim() || task.title || t("todo.untitledTask")}
          </h3>
        </div>
        <button type="button" className="td-detail-aside-close" onClick={requestClose} aria-label={t("common.close")}>
          <span aria-hidden="true">×</span>
          <span className="td-detail-aside-close-label">{t("common.close")}</span>
        </button>
      </div>

      <div className="td-detail-id">
        <span className="td-detail-id-num">{shortTaskId(task)}</span>
        <span style={{ color: "var(--td-text-4)" }}>·</span>
        <span>
          {task.created_at ? formatDateTimeShort(task.created_at, t) : ""}
          {task.created_by?.name ? ` · ${task.created_by.name}` : ""}
        </span>
      </div>

      <div className="td-form-field">
        <label className="td-form-label" htmlFor="td-detail-title-input">
          {t("todo.createTitlePlaceholder")}
        </label>
        <input
          id="td-detail-title-input"
          className="td-field"
          value={titleDraft}
          maxLength={TODO_TASK_TITLE_MAX_LENGTH}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.target.blur();
            }
          }}
          placeholder={t("todo.createTitlePlaceholder")}
          spellCheck={false}
        />
      </div>

      <div className="td-form-field">
        <span className="td-form-label">{t("todo.descriptionLabel")}</span>
        <PromangRichTextEditor
          value={descDraft}
          onChange={handleDescChange}
          placeholder={t("todo.descriptionPlaceholder")}
          size="md"
          framed
          templatesEnabled={false}
          wrapperClassName="td-rich-editor"
        />
      </div>

      <div className="td-detail-section-label">{t("todo.propertiesLabel")}</div>

      {task.shared ? (
        <div className="td-prop td-shared-prop is-shared-active">
          <PropertyLabelWithInfo
            label={t("todo.sharedTaskLabel")}
            onInfo={() =>
              setPropertyInfo({ title: t("todo.sharedTaskLabel"), text: sharedInfoText })
            }
            infoAriaLabel={t("todo.propertyInfoAria", { label: t("todo.sharedTaskLabel") })}
          />
          <div className="td-prop-value">
            <span className="td-shared-active-badge" role="status">
              <span className="td-chip-dot" style={{ background: "#2b6cff" }} />
              <span>{t("todo.sharedTaskActiveBadge")}</span>
            </span>
          </div>
        </div>
      ) : null}

      {!isTaskAssigneesLocked(task) ? (
        <div className="td-prop">
          <PropertyLabelWithInfo
            label={t("todo.peopleLabel")}
            onInfo={() =>
              setPropertyInfo({ title: t("todo.peopleLabel"), text: t("todo.taskAssigneesHint") })
            }
            infoAriaLabel={t("todo.propertyInfoAria", { label: t("todo.peopleLabel") })}
          />
          <div className="td-prop-value td-prop-people">
            {peopleTags.map((row) => (
              <UserAssigneeTag key={row.id} name={row.name || `#${row.id}`} />
            ))}
            {peopleEditable ? (
              <button
                type="button"
                className={`${planGhostBtnClass} td-chip-btn td-user-tag-add`}
                onClick={() => setAssigneePickerOpen(true)}
                aria-label={t("todo.createAddAssignees")}
              >
                <span aria-hidden>+</span>
                <span>{peopleTags.length ? t("todo.settingsAddAssignees") : t("todo.createAddAssignees")}</span>
              </button>
            ) : null}
            {!peopleEditable && peopleTags.length === 0 ? (
              <span className="td-prop-value-empty">—</span>
            ) : null}
          </div>
        </div>
      ) : null}

      {task.shared && canManageSharedTasks ? (
        <div className="td-form-field">
          <HseDocumentSelector
            showField
            name="task_reference_id"
            label="todo.settingsHseDocument"
            selected={
              task.task_reference_id
                ? { id: task.task_reference_id, displayTitle: `#${task.task_reference_id}` }
                : null
            }
            onChange={(event) => {
              const docId = event?.target?.value || null;
              onUpdate(task.id, {
                task_reference_id: docId,
                task_reference_type: docId ? "Hse::Document" : "",
              });
            }}
          />
        </div>
      ) : null}

      <div className="td-prop">
        <div className="td-prop-label">{t("todo.statusLabel")}</div>
        <div className="td-prop-value">
          <div className="td-anchor">
            <button
              type="button"
              className={`${planGhostBtnClass} td-chip-btn`}
              onClick={() => setPropPop(propPop === "status" ? null : "status")}
            >
              <StatusGlyph status={status} t={t} />
              <span>{t(statusObj.labelKey)}</span>
            </button>
            {propPop === "status" && (
              <div className="td-popover">
                <div
                  className={`td-popover-item ${status === "todo" ? "is-active" : ""}`}
                  onClick={() => setProp({ finished: false, on_today_plan: false })}
                >
                  <span style={{ color: findStatus("todo").color }}>◔</span>
                  <span>{t("todo.statusTodo")}</span>
                </div>
                <div
                  className={`td-popover-item ${status === "doing" ? "is-active" : ""}`}
                  onClick={() => setProp({ finished: false, on_today_plan: true })}
                >
                  <span style={{ color: findStatus("doing").color }}>◑</span>
                  <span>{t("todo.statusDoing")}</span>
                </div>
                <div
                  className={`td-popover-item ${status === "done" ? "is-active" : ""}`}
                  onClick={() => setProp({ finished: true })}
                >
                  <span style={{ color: findStatus("done").color }}>●</span>
                  <span>{t("todo.statusDone")}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="td-prop">
        <div className="td-prop-label">{t("todo.priorityLabel")}</div>
        <div className="td-prop-value">
          <div className="td-anchor">
            <button
              type="button"
              className={`${planGhostBtnClass} td-chip-btn`}
              onClick={() => setPropPop(propPop === "priority" ? null : "priority")}
            >
              <PriorityGlyph priority={task.priority} t={t} />
              <span>{t(priorityObj.labelKey)}</span>
            </button>
            {propPop === "priority" && (
              <div className="td-popover">
                {PRIORITIES.map((p) => (
                  <div
                    key={p.id}
                    className={`td-popover-item ${p.id === priorityObj.id ? "is-active" : ""}`}
                    onClick={() => setProp({ priority: p.id })}
                  >
                    <span
                      style={{
                        color: p.color,
                        fontFamily: "var(--td-font-mono)",
                        width: 18,
                        textAlign: "center",
                        display: "inline-block",
                      }}
                    >
                      {p.glyph}
                    </span>
                    <span>{t(p.labelKey)}</span>
                    {p.id === priorityObj.id && <span className="td-popover-check">✓</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="td-prop">
        <div className="td-prop-label">{t("todo.moduleLabel")}</div>
        <div className="td-prop-value">
          <ModuleChip moduleObj={moduleObj} t={t} />
        </div>
      </div>

      <div className="td-prop">
        <div className="td-prop-label">{t("todo.dueDate")}</div>
        <div className="td-prop-value">
          <div className="td-anchor">
            <button
              type="button"
              className={`${planGhostBtnClass} td-chip-btn`}
              onClick={() => setPropPop(propPop === "due" ? null : "due")}
            >
              <span style={{ color: "var(--td-text-3)" }}>◷</span>
              <span>{task.due_date ? formatSmartDueDate(task.due_date, t) : t("todo.setDate")}</span>
            </button>
            {propPop === "due" && (
              <div className="td-popover">
                <DateQuickPicker
                  mode="date"
                  value={task.due_date || null}
                  onChange={(v) => setProp({ due_date: v || null })}
                  onClear={() => setProp({ due_date: null })}
                  t={t}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="td-prop">
        <div className="td-prop-label">{t("todo.reminder")}</div>
        <div className="td-prop-value">
          <div className="td-anchor">
            <button
              type="button"
              className={`${planGhostBtnClass} td-chip-btn`}
              onClick={() => setPropPop(propPop === "reminder" ? null : "reminder")}
            >
              <span style={{ color: task.reminder_at ? "var(--td-accent)" : "var(--td-text-3)" }}>◔</span>
              <span>
                {task.reminder_at ? formatSmartReminder(task.reminder_at, t) : t("todo.remindMe")}
              </span>
            </button>
            {propPop === "reminder" && (
              <div className="td-popover">
                <DateQuickPicker
                  mode="datetime"
                  value={task.reminder_at || null}
                  onChange={(v) => setProp({ reminder_at: v || null })}
                  onClear={() => setProp({ reminder_at: null })}
                  t={t}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="td-prop">
        <div className="td-prop-label">{t("todo.todayPlanBadge")}</div>
        <div className="td-prop-value">
          <button
            type="button"
            className={`${planGhostBtnClass} td-chip-btn ${task.on_today_plan ? "is-set" : ""}`}
            onClick={() => onUpdate(task.id, { on_today_plan: !task.on_today_plan })}
          >
            <span>◑</span>
            <span>{task.on_today_plan ? t("todo.toggleOn") : t("todo.toggleOff")}</span>
          </button>
        </div>
      </div>

      <div className="td-prop">
        <div className="td-prop-label">{t("todo.recurrence")}</div>
        <div className="td-prop-value">
          <div className="td-anchor">
            <button
              type="button"
              className={`${planGhostBtnClass} td-chip-btn`}
              onClick={() => setPropPop(propPop === "recurrence" ? null : "recurrence")}
            >
              <span style={{ fontFamily: "var(--td-font-mono)", fontSize: 11, color: "var(--td-text-3)" }}>↻</span>
              <span>
                {t(
                  RECURRENCE_OPTIONS.find((o) => o.value === (task.recurrence || "none"))?.labelKey ||
                    "todo.recurrenceNone"
                )}
              </span>
            </button>
            {propPop === "recurrence" && (
              <div className="td-popover">
                {RECURRENCE_OPTIONS.map((opt) => (
                  <div
                    key={opt.value}
                    className={`td-popover-item ${(task.recurrence || "none") === opt.value ? "is-active" : ""}`}
                    onClick={() => setProp({ recurrence: opt.value })}
                  >
                    <span style={{ color: "var(--td-text-4)" }}>↻</span>
                    <span>{t(opt.labelKey)}</span>
                    {(task.recurrence || "none") === opt.value && (
                      <span className="td-popover-check">✓</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="td-prop">
        <div className="td-prop-label">{t("todo.estimatedHours")}</div>
        <div className="td-prop-value">
          <input
            type="number"
            min="0"
            step="0.25"
            className="td-field td-prop-row-input"
            defaultValue={task.estimated_hours ?? ""}
            onBlur={(e) => {
              const v = e.target.value === "" ? null : e.target.value;
              if (v !== (task.estimated_hours ?? null)) onUpdate(task.id, { estimated_hours: v });
            }}
          />
        </div>
      </div>

      {(task.absence || (task?.description && task.description.includes("Item List ID:")) || hasHseDocumentLink(task)) && (
        <>
          <div className="td-detail-section-label">{t("todo.systemLinksLabel")}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
            {task.absence && (
              <span
                className="td-chip td-detail-link"
                onClick={() => navigate(`/dashboard/absences/${task.absence.id}`)}
              >
                <span className="td-chip-dot" style={{ background: "#fbbf24" }} />
                <span className="td-chip-text">
                  {t("todo.absenceLink")}:{" "}
                  {task.absence.displayTitle || task.absence.absence_number || `#${task.absence.id}`}
                </span>
              </span>
            )}
            {task?.description && task.description.includes("Item List ID:") && (() => {
              const itemListIdMatch = task.description.match(/Item List ID: (\d+)/);
              const itemListId = itemListIdMatch ? itemListIdMatch[1] : null;
              if (!itemListId) return null;
              return (
                <span
                  className="td-chip td-detail-link"
                  onClick={() => navigate(`/dashboard/inventory/item-handling/${itemListId}`)}
                >
                  <span className="td-chip-dot" style={{ background: "#a78bfa" }} />
                  <span className="td-chip-text">
                    {t("todo.itemListLink")}: {task.title?.replace("Håndter vareliste - ", "") || ""}
                  </span>
                </span>
              );
            })()}
            {hasHseDocumentLink(task) && task.task_reference_id && (
              <span
                className="td-chip td-detail-link"
                onClick={() => navigate(`/dashboard/hse-ks/documents/${task.task_reference_id}`)}
              >
                <span className="td-chip-dot" style={{ background: "#f472b6" }} />
                <span className="td-chip-text">{t("todo.hseDocumentOpenLink")}</span>
              </span>
            )}
          </div>
        </>
      )}

      <div className="td-detail-section-label">{t("todo.moduleTagsLabel")}</div>
      <div
        style={{
          background: "var(--td-surface-2)",
          padding: 8,
          borderRadius: 6,
          border: "1px solid var(--td-border)",
          marginBottom: 12,
        }}
      >
        <TodoModuleTagField
          value={moduleTags}
          onChange={(next) => {
            setModuleTags(next);
            onUpdate(task.id, { module_tags: moduleTagsPayload(next) });
          }}
        />
      </div>

      <div className="td-detail-section-label">{t("todo.labels")}</div>
      <div
        style={{
          background: "var(--td-surface-2)",
          padding: 8,
          borderRadius: 6,
          border: "1px solid var(--td-border)",
        }}
      >
        <CaseTagSelector
          scopeModule="task"
          value={taskTagIds}
          onChange={(ids) => {
            setTaskTagIds(ids);
            onUpdate(task.id, { tag_ids: ids });
          }}
          showBadges
        />
      </div>

      <div className="td-aside-section">
        <h3>{t("todo.statusTimeline")}</h3>
        <StatusTimeline task={task} t={t} />
      </div>

      <div className="td-aside-section">
        <h3>{t("todo.activityLabel")}</h3>
        <ActivityLog task={task} t={t} />
      </div>

      <div className="td-detail-ai">
        <div className="td-detail-ai-head">
          <span>✦</span>
          <span>{t("todo.aiAssistant")}</span>
        </div>
        <input
          className="td-detail-ai-hours"
          type="text"
          value={hoursAvailable}
          onChange={(e) => setHoursAvailable(e.target.value)}
          placeholder={t("todo.hoursAvailable")}
          aria-label={t("todo.hoursAvailable")}
        />
        <div className="td-detail-ai-row">
          <button type="button" className="td-detail-ai-btn" onClick={() => runTodoAi(["task_suggestions"])}>
            {t("todo.aiSuggestions")}
          </button>
          <button type="button" className="td-detail-ai-btn" onClick={() => runTodoAi(["time_estimate"])}>
            {t("todo.aiEstimate")}
          </button>
          <button type="button" className="td-detail-ai-btn" onClick={() => runTodoAi(["daily_plan"])}>
            {t("todo.aiPlan")}
          </button>
        </div>
      </div>

      <div className="td-detail-danger">
        {task.shared && !canManageSharedTasks ? (
          <p className="td-detail-danger-note">{t("todo.sharedTaskDeleteAdminOnly")}</p>
        ) : (
          <button type="button" className="td-detail-danger-btn" onClick={() => onDelete(task.id)}>
            <span>🗑</span>
            <span>{t("todo.deleteTask")}</span>
          </button>
        )}
      </div>

      </aside>

      <ModernEmployeeSelector
        open={assigneePickerOpen}
        useDialog
        multiple
        closeOnSelect={false}
        closeOnOutsideClick={false}
        selectedEmployees={selectedAssignees}
        onClose={() => setAssigneePickerOpen(false)}
        onConfirm={(employees) => {
          setSelectedAssignees(employees);
          onUpdate(task.id, {
            assignee_user_ids: employees.map((row) => row.id),
          });
        }}
      />

      <UnsavedChangesDialog
        open={confirmClose}
        title={t("todo.confirmClose.title")}
        message={t("todo.confirmClose.text")}
        keepEditingText={t("todo.confirmClose.keepEditing")}
        closeWithoutSavingText={t("todo.confirmClose.closeWithoutSaving")}
        saveText={t("todo.confirmClose.saveAndClose")}
        onKeepEditing={() => setConfirmClose(false)}
        onCloseWithoutSaving={() => {
          setConfirmClose(false);
          onClose();
        }}
        onSave={saveAndClose}
      />

      <PropertyInfoDialog
        open={!!propertyInfo}
        title={propertyInfo?.title || ""}
        text={propertyInfo?.text || ""}
        onClose={() => setPropertyInfo(null)}
        t={t}
      />
    </>
  );
}

/* ─────────────────────────────────────────────
 * Main component
 * ──────────────────────────────────────────── */

export const TodoTaskList = ({ hideHeader = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session?.currentUser);
  const canManageSharedTasks = useMemo(
    () => currentUser?.roles?.some((role) => role.name === "admin") || false,
    [currentUser?.roles]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = parseTodoFilter(searchParams);
  const showMine = parseTodoMine(searchParams);
  const showTodayPlan = parseTodoTodayPlan(searchParams);
  const groupBy = parseTodoGroupBy(searchParams);

  const patchTodoParams = useCallback(
    (patch) => {
      setSearchParams((prev) => mergeTodoWorkspaceParams(prev, patch), { replace: true });
    },
    [setSearchParams]
  );

  const [tasks, setTasks] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const scrollContainerRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const loadingRef = useRef(loading);
  const loadingMoreRef = useRef(false);
  const hasMoreRef = useRef(hasMore);
  const pageRef = useRef(page);
  const fetchTasksPageRef = useRef(null);
  const finishInflightRef = useRef(new Set());

  const [selectedTask, setSelectedTask] = useState(null);
  const [nextDuePrompt, setNextDuePrompt] = useState(null);
  const [nextDueBusy, setNextDueBusy] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [taskTagIds, setTaskTagIds] = useState([]);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState("");
  const [hoursAvailable, setHoursAvailable] = useState("6");

  const fetchTasksPage = useCallback(
    async ({ page: pageNum, append }) => {
      const params = { page: pageNum, per_page: TODO_PAGE_SIZE };
      if (filter === "active") params.finished = false;
      else if (filter === "finished") params.finished = true;
      if (showMine) params.mine = true;
      if (showTodayPlan) params.today_plan = true;

      if (append) {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await tasksApi.getTasks(params);
        if (response?.data?.success) {
          const rows = response.data.data || [];
          const meta = response.data.meta || {};
          const total = typeof meta.total === "number" ? meta.total : rows.length;
          setTotalCount(total);
          setError(null);

          let nextLength = 0;
          setTasks((prev) => {
            const next = append ? [...prev, ...rows] : rows;
            nextLength = next.length;
            return next;
          });
          const more = nextLength < total;
          setHasMore(more);
          hasMoreRef.current = more;
          setPage(pageNum);
          pageRef.current = pageNum;
        } else {
          setError(t("todo.loadError"));
          if (!append) setTasks([]);
          hasMoreRef.current = false;
        }
      } catch (err) {
        console.error("Error fetching tasks:", err);
        if (append) {
          toast.error(i18n.t("todo.loadMoreError"));
        } else {
          setError(t("todo.loadError"));
          setTasks([]);
          hasMoreRef.current = false;
        }
      } finally {
        loadingMoreRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filter, showMine, showTodayPlan, i18n, t]
  );

  loadingRef.current = loading;
  hasMoreRef.current = hasMore;
  pageRef.current = page;
  fetchTasksPageRef.current = fetchTasksPage;

  useEffect(() => {
    fetchTasksPage({ page: 1, append: false });
  }, [filter, showMine, showTodayPlan, fetchTasksPage]);

  useEffect(() => {
    if (loading && tasks.length === 0) return undefined;
    const root = scrollContainerRef.current;
    const target = loadMoreSentinelRef.current;
    if (!root || !target) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        if (loadingRef.current || loadingMoreRef.current || !hasMoreRef.current) return;
        const nextPage = pageRef.current + 1;
        void fetchTasksPageRef.current?.({ page: nextPage, append: true });
      },
      { root, rootMargin: "120px", threshold: 0 }
    );
    io.observe(target);
    return () => io.disconnect();
  }, [loading, filter, showMine, showTodayPlan, tasks.length]);

  // Global "N" hotkey opens create modal.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      const inEditable = tag === "input" || tag === "textarea" || e.target.isContentEditable;
      if (inEditable) return;
      if ((e.key === "n" || e.key === "N") && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setIsCreating(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleCreateTask = useCallback(
    async (payload) => {
      const tempId = `temp-${Date.now()}`;
      const optimistic = {
        id: tempId,
        title: payload.title,
        description: payload.description || "",
        priority: payload.priority || "med",
        finished: false,
        active: true,
        on_today_plan: !!payload.on_today_plan,
        due_date: payload.due_date || null,
        reminder_at: payload.reminder_at || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      setTasks((prev) => [optimistic, ...prev]);
      setIsCreating(false);

      try {
        const response = await tasksApi.createTask(payload);
        if (response?.data?.success) {
          setTasks((prev) => prev.map((task) => (task.id === tempId ? response.data.data : task)));
          setTotalCount((c) => c + 1);
          toast.success(t("todo.toastCreated"));
        } else {
          setTasks((prev) => prev.filter((task) => task.id !== tempId));
          toast.error(t("todo.toastCreateFailed"));
        }
      } catch (err) {
        console.error("Error creating task:", err);
        setTasks((prev) => prev.filter((task) => task.id !== tempId));
        toast.error(t("todo.toastCreateFailed"));
      }
    },
    [t]
  );

  const handleUpdateTask = useCallback(
    async (taskId, updates) => {
      const previousTasks = tasks;
      const previousSelected = selectedTask;
      setTasks((prev) =>
        prev.map((task) =>
          task.id === taskId ? { ...task, ...updates, updated_at: new Date().toISOString() } : task
        )
      );
      if (selectedTask?.id === taskId) {
        setSelectedTask((prev) => ({ ...prev, ...updates }));
      }
      try {
        const response = await tasksApi.updateTask(taskId, updates);
        if (response?.data?.success) {
          setTasks((prev) => prev.map((task) => (task.id === taskId ? response.data.data : task)));
          if (selectedTask?.id === taskId) {
            setSelectedTask(response.data.data);
            setTaskTagIds((response.data.data.tag_definitions || []).map((x) => x.id));
          }
        } else {
          setTasks(previousTasks);
          if (previousSelected?.id === taskId) setSelectedTask(previousSelected);
          toast.error(response?.data?.errors?.[0] || t("todo.toastUpdateFailed"));
        }
      } catch (err) {
        console.error("Error updating task:", err);
        setTasks(previousTasks);
        if (previousSelected?.id === taskId) setSelectedTask(previousSelected);
        toast.error(err?.response?.data?.errors?.[0] || t("todo.toastUpdateFailed"));
      }
    },
    [tasks, selectedTask, t]
  );

  const handleDeleteTask = useCallback(
    async (taskId) => {
      const task = tasks.find((row) => row.id === taskId);
      if (task?.shared && !canManageSharedTasks) {
        toast.error(t("todo.sharedTaskDeleteAdminOnly"));
        return;
      }
      if (!window.confirm(t("todo.confirmDelete"))) return;
      const previousTasks = tasks;
      setTasks((prev) => prev.filter((task) => task.id !== taskId));
      if (selectedTask?.id === taskId) setSelectedTask(null);

      try {
        const response = await tasksApi.deleteTask(taskId);
        if (response?.data?.success) {
          setTotalCount((c) => Math.max(0, c - 1));
          toast.success(t("todo.toastDeleted"));
        } else {
          setTasks(previousTasks);
          toast.error(response?.data?.errors?.[0] || t("todo.toastDeleteFailed"));
        }
      } catch (err) {
        console.error("Error deleting task:", err);
        setTasks(previousTasks);
        toast.error(err?.response?.data?.errors?.[0] || t("todo.toastDeleteFailed"));
      }
    },
    [tasks, selectedTask, canManageSharedTasks, t]
  );

  const handleToggleFinished = useCallback(
    async (taskId, currentStatus) => {
      if (finishInflightRef.current.has(taskId)) return;
      finishInflightRef.current.add(taskId);
      const task = tasks.find((x) => x.id === taskId);
      if (!task) return;

      if (task.absence_id) {
        const absence = task.absence;
        if (!absence?.accepted && !absence?.denied) {
          toast.error(t("todo.lockedAbsence"));
          return;
        }
      }
      if (isItemListLockedTask(task)) {
        toast.error(t("todo.lockedItemList"));
        return;
      }
      const hseDocumentTask = isHseDocumentModuleTask(task);
      if (hseDocumentTask && currentStatus) {
        toast.error(t("todo.hseDocumentTaskCannotReopen"));
        return;
      }

      const newStatus = !currentStatus;
      setTasks((prev) => prev.map((x) => (x.id === taskId ? { ...x, finished: newStatus } : x)));
      if (selectedTask?.id === taskId) setSelectedTask((prev) => ({ ...prev, finished: newStatus }));

      try {
        const response = await tasksApi.toggleFinished(taskId);
        if (response?.data?.success) {
          setTasks((prev) => prev.map((x) => (x.id === taskId ? response.data.data : x)));
          if (selectedTask?.id === taskId) setSelectedTask(response.data.data);
          if (hseDocumentTask && newStatus) {
            toast.success(t("hse.document.acknowledge_read_success"));
          }
        } else {
          setTasks((prev) => prev.map((x) => (x.id === taskId ? { ...x, finished: currentStatus } : x)));
          if (selectedTask?.id === taskId) setSelectedTask((prev) => ({ ...prev, finished: currentStatus }));
          toast.error(t("todo.toastStatusFailed"));
        }
      } catch (err) {
        console.error("Error toggling finished status:", err);
        setTasks((prev) => prev.map((x) => (x.id === taskId ? { ...x, finished: currentStatus } : x)));
        if (selectedTask?.id === taskId) setSelectedTask((prev) => ({ ...prev, finished: currentStatus }));
        if (err?.response?.data?.requires_next_due) {
          setNextDuePrompt({
            taskId,
            currentStatus,
            suggested: err.response.data.suggested_due_date,
          });
          return;
        }
        toast.error(t("todo.toastStatusFailed"));
      } finally {
        finishInflightRef.current.delete(taskId);
      }
    },
    [tasks, selectedTask, t]
  );

  const openTask = useCallback((task) => {
    setSelectedTask(task);
    setTaskTagIds((task.tag_definitions || []).map((x) => x.id));
  }, []);

  const runTodoAi = useCallback(
    async (fields) => {
      if (!selectedTask) return;
      setAiLoading(true);
      setAiOpen(true);
      setAiText("");
      try {
        const openTasksSummary = tasks
          .filter((x) => !x.finished)
          .map((x) => x.title)
          .filter(Boolean)
          .slice(0, 40)
          .join("\n");
        const res = await generateAIContent({
          module: "todo",
          context: {
            task_title: selectedTask.title || "",
            task_description: selectedTask.description || "",
            open_tasks: openTasksSummary,
            hours_available: hoursAvailable || "6",
          },
          fields_to_generate: fields,
          options: { language: "no", length: "short" },
        });
        if (res?.success && res.generated_fields) {
          const parts = Object.entries(res.generated_fields).map(([k, v]) => `${k.toUpperCase()}\n${v}`);
          setAiText(parts.join("\n\n"));
        } else {
          setAiText(res?.errors?.join?.("\n") || t("todo.aiError"));
        }
      } catch (e) {
        console.error(e);
        const apiErrors = e?.response?.data?.errors;
        setAiText(Array.isArray(apiErrors) && apiErrors.length ? apiErrors.join("\n") : t("todo.aiError"));
      } finally {
        setAiLoading(false);
      }
    },
    [selectedTask, tasks, hoursAvailable, t]
  );

  useEffect(() => {
    if (parseTodoCreate(searchParams)) {
      setIsCreating(true);
      setSearchParams((prev) => mergeTodoWorkspaceParams(prev, { create: null }), { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const grouped = useMemo(() => {
    if (groupBy === "priority") {
      const buckets = {};
      PRIORITIES.forEach((p) => {
        buckets[p.id] = [];
      });
      tasks.forEach((task) => {
        const id = findPriority(task.priority).id;
        if (buckets[id]) buckets[id].push(task);
      });
      return PRIORITIES.map((p) => ({
        status: p,
        tasks: buckets[p.id],
        groupKind: "priority",
      })).filter((group) => group.tasks.length > 0);
    }

    const buckets = { doing: [], todo: [], done: [] };
    tasks.forEach((task) => {
      const id = deriveStatus(task);
      if (buckets[id]) buckets[id].push(task);
    });
    return STATUS_ORDER.map((id) => ({
      status: findStatus(id),
      tasks: buckets[id],
      groupKind: "status",
    })).filter((group) => group.tasks.length > 0);
  }, [tasks, groupBy]);

  const filterPills = [
    { id: "all", labelKey: "todo.filterAll" },
    { id: "active", labelKey: "todo.filterActive" },
    { id: "finished", labelKey: "todo.filterFinished" },
  ];

  const filterBar = (
    <div className="td-filter-bar td-filter-bar-desktop">
      <div className="td-filter-bar-main">
        <div className="td-filter-switcher" role="tablist" aria-label={t("todo.filterAll")}>
          {filterPills.map((pill) => (
            <button
              key={pill.id}
              type="button"
              role="tab"
              aria-selected={filter === pill.id}
              className={`td-filter-seg ${filter === pill.id ? "is-active" : ""}`}
              onClick={() => patchTodoParams({ status: pill.id })}
            >
              <span>{t(pill.labelKey)}</span>
              {filter === pill.id && totalCount > 0 ? (
                <span className="td-filter-count">{totalCount}</span>
              ) : null}
            </button>
          ))}
        </div>
        {!hideHeader ? (
          <button
            type="button"
            className={`pp-btn td-filter-mine ${showMine ? "is-active" : ""}`}
            aria-pressed={showMine}
            onClick={() => patchTodoParams({ mine: !showMine })}
          >
            {t("todo.filterMine")}
          </button>
        ) : null}
      </div>
      <div className="td-filter-bar-aside">
        {hideHeader ? <TodoWorkspaceQuickActions /> : null}
        {canManageSharedTasks ? (
          <Link
            to="/dashboard/todos/settings"
            className="pp-btn td-filter-settings"
            title={t("todo.settingsTitle")}
            aria-label={t("todo.settingsTitle")}
          >
            <Cog6ToothIcon className="h-4 w-4" />
          </Link>
        ) : null}
        <button type="button" className={`pp-btn pp-btn-primary td-filter-new`} onClick={() => setIsCreating(true)}>
          <span>+ {t("todo.newTask")}</span>
          <kbd className="td-kbd">N</kbd>
        </button>
      </div>
    </div>
  );

  return (
    <div
      className={`project-plan-sitelane td-app${hideHeader ? " td-app-embedded h-full min-h-0 flex-1" : ""}`}
    >
      {!hideHeader && <DashboardHeader title={t("todo.pageTitle")} />}

      <div className="td-main">
        {!hideHeader ? (
          <div className="td-topbar">
            <div className="td-topbar-title">
              <span className="td-topbar-title-glyph">≡</span>
              <span>{t("todo.tabTasks")}</span>
            </div>
          </div>
        ) : null}
        {filterBar}

        <div className="td-body">
          <div ref={scrollContainerRef} className="td-list-wrap">
        {!hideHeader ? (
          <div className="td-list-toolbar td-filter-bar-desktop">
            <span style={{ fontFamily: "var(--td-font-mono)", fontSize: 11, color: "var(--td-text-4)" }}>
              {tasks.length} {t(tasks.length === 1 ? "todo.taskCountOne" : "todo.taskCountOther")}
            </span>
            <span style={{ color: "var(--td-text-4)" }}>·</span>
            <span className="td-list-toolbar-filter">
              <span className="td-list-toolbar-filter-glyph">⌖</span>
              <span>{t("todo.groupBy")}</span>
              <span style={{ color: "var(--td-text)" }}>
                {groupBy === "priority" ? t("todo.priorityLabel") : t("todo.statusLabel")}
              </span>
            </span>
          </div>
        ) : null}

            {loading && tasks.length === 0 ? (
              <div className="td-empty">
                <div className="td-empty-text">{t("todo.loadingTasks")}</div>
              </div>
            ) : error && tasks.length === 0 ? (
              <div className="td-empty">
                <div className="td-empty-glyph">⚠</div>
                <div className="td-empty-text">{error}</div>
              </div>
            ) : grouped.length === 0 ? (
              <div className="td-empty">
                <div className="td-empty-glyph">◌</div>
                <div className="td-empty-text">
                  {t("todo.emptyState")} <kbd>N</kbd>
                </div>
              </div>
            ) : (
              grouped.map((group) => (
                <div key={group.status.id}>
                  <div className="td-list-group-head">
                    <span style={{ color: group.status.color }}>{group.status.glyph}</span>
                    <span>{t(group.status.labelKey)}</span>
                    <span className="td-list-group-head-count">{group.tasks.length}</span>
                    <span className="td-list-group-head-rule" />
                  </div>
                  {group.tasks.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      isSelected={selectedTask?.id === task.id}
                      onOpen={openTask}
                      onToggle={handleToggleFinished}
                      onToggleTodayPlan={(taskId, onTodayPlan) =>
                        handleUpdateTask(taskId, { on_today_plan: onTodayPlan })
                      }
                      t={t}
                    />
                  ))}
                </div>
              ))
            )}

            {hasMore && !loading && tasks.length > 0 && (
              <div ref={loadMoreSentinelRef} className="td-load-more-sentinel" aria-hidden />
            )}
            {loadingMore && <div className="td-loading-more">{t("todo.loadingMoreTodos")}</div>}
          </div>

          {selectedTask && (
            <DetailAside
              task={selectedTask}
              onClose={() => setSelectedTask(null)}
                onUpdate={handleUpdateTask}
                onToggleFinished={handleToggleFinished}
                onDelete={handleDeleteTask}
                taskTagIds={taskTagIds}
                setTaskTagIds={setTaskTagIds}
                runTodoAi={runTodoAi}
                hoursAvailable={hoursAvailable}
                setHoursAvailable={setHoursAvailable}
                canManageSharedTasks={canManageSharedTasks}
                currentUserId={currentUser?.id}
                t={t}
              navigate={navigate}
            />
          )}
        </div>
      </div>

      <CreateModal
        open={isCreating}
        onClose={() => setIsCreating(false)}
        onCreate={handleCreateTask}
        t={t}
      />

      {aiOpen && (
        <div
          className="project-plan-sitelane td-app td-create-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setAiOpen(false);
          }}
          role="presentation"
        >
          <div
            className="td-create-card"
            onMouseDown={(e) => e.stopPropagation()}
            style={{ maxWidth: 540 }}
          >
            <div className="td-create-head">
              <span>{t("todo.aiResultTitle")}</span>
              <button
                type="button"
                className={`${planGhostBtnClass} td-icon-btn`}
                onClick={() => setAiOpen(false)}
                aria-label={t("common.close")}
              >
                ×
              </button>
            </div>
            <div style={{ padding: 18 }}>
              {aiLoading ? (
                <div style={{ color: "var(--td-text-3)" }}>{t("todo.aiLoading")}</div>
              ) : (
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    fontFamily: "var(--td-font-serif)",
                    fontSize: 14,
                    color: "var(--td-text)",
                    margin: 0,
                    lineHeight: 1.55,
                  }}
                >
                  {aiText}
                </pre>
              )}
            </div>
            <div className="td-create-foot">
              <div style={{ flex: 1 }} />
              <button type="button" className={planPrimaryBtnClass} onClick={() => setAiOpen(false)}>
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}
      <OverdueRecurrenceDialog
        open={!!nextDuePrompt}
        suggestedDue={nextDuePrompt?.suggested}
        busy={nextDueBusy}
        onCancel={() => setNextDuePrompt(null)}
        onConfirm={async (nextDueDate) => {
          if (!nextDuePrompt) return;
          setNextDueBusy(true);
          try {
            const response = await tasksApi.toggleFinished(nextDuePrompt.taskId, {
              next_due_date: nextDueDate,
            });
            if (response?.data?.success) {
              setTasks((prev) =>
                prev.map((x) => (x.id === nextDuePrompt.taskId ? response.data.data : x))
              );
              if (selectedTask?.id === nextDuePrompt.taskId) setSelectedTask(response.data.data);
              setNextDuePrompt(null);
            } else {
              toast.error(t("todo.toastStatusFailed"));
            }
          } catch (err) {
            toast.error(err?.response?.data?.errors?.[0] || t("todo.toastStatusFailed"));
          } finally {
            setNextDueBusy(false);
          }
        }}
      />
    </div>
  );
};
